import { DB_CONFIG } from '../constants/database';
import { LocalStorageService } from './local-storage';

export class SubscriptionService {
  private static instance: SubscriptionService;
  private registration: ServiceWorkerRegistration | undefined = undefined;
  private auroraLevel: string = 'SOME';
  private stations: string[] = [];

  private static readonly BASE_URL = import.meta.env.DEV ? '/webpush' : 'https://aurora.narwhal.tech/webpush';

  private constructor() {
    // Load persisted settings
    this.loadSettings();
  }

  public static getInstance(): SubscriptionService {
    if (!SubscriptionService.instance) {
      SubscriptionService.instance = new SubscriptionService();
    }
    return SubscriptionService.instance;
  }

  private loadSettings(): void {
    try {
      const level = LocalStorageService.getAuroraLevel();
      this.auroraLevel = level.toUpperCase();

      const stations = LocalStorageService.getStations();
      this.stations = stations;
    } catch (error) {
      console.error('Failed to load subscription settings:', error);
    }
  }

  private saveSettings(): void {
    try {
      LocalStorageService.saveAuroraLevel(this.auroraLevel);
      LocalStorageService.saveStations(this.stations);
    } catch (error) {
      console.error('Failed to save subscription settings:', error);
    }
  }


  /**
   * Get full subscription payload with credentials, level, and stations
   */
  private getFullSubscriptionPayload(subscription: PushSubscription): Record<string, any> {
    return {
      endpoint: subscription.endpoint,
      p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
      auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))),
      level: this.auroraLevel,
      stations: this.stations,
    };
  }

  public async requestPermission(): Promise<NotificationPermission> {
    if (typeof Notification === 'undefined') {
      return 'denied';
    }
    return await Notification.requestPermission();
  }

  public async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    console.log('Registering service worker...');

    if (!this.registration) {
      try {
        // Check if there's already a registration for our service worker
        this.registration = await navigator.serviceWorker.getRegistration('/');

        if (!this.registration) {
          // Register the new service worker with explicit scope
          this.registration = await navigator.serviceWorker.register('/sw.js', {
            scope: '/'
          });
        }

        // Wait for the service worker to be ready
        await navigator.serviceWorker.ready;

      } catch (error) {
        console.error('Error registering service worker:', error);
        throw error;
      }
    }
    return this.registration;
  }

  public async subscribeToPushNotifications(publicKey: string): Promise<PushSubscription> {
    const registration = await this.registerServiceWorker();

    // Check if pushManager is available
    if (!registration.pushManager) {
      throw new Error('Push manager not available');
    }

    // Convert VAPID key to Uint8Array
    const applicationServerKey = this.urlBase64ToUint8Array(publicKey) as Uint8Array<ArrayBuffer>;
    console.debug('VAPID key converted:', applicationServerKey);

    // Returns existing subscription if available or create a new one
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey
    });

    console.debug('Push subscription created:', subscription);

    // Send subscription to server with full payload (credentials + level + stations)
    const payload = this.getFullSubscriptionPayload(subscription);

    const response = await fetch(`${SubscriptionService.BASE_URL}/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Failed to register subscription with server: ${response.status} ${response.statusText}`);
    }

    console.debug('Successfully registered subscription with server');
    return subscription;
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    // Remove any whitespace and ensure the string is properly padded
    const base64 = base64String.trim().replace(/\s/g, '');
    const padding = '='.repeat((4 - base64.length % 4) % 4);
    const base64Url = (base64 + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64Url);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  public async showNotification(title: string, options?: NotificationOptions): Promise<void> {
    console.log('Showing notification:', title, options);

    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        const registration = await this.registerServiceWorker();

        const notificationOptions = {
          body: options?.body || 'New notification',
          icon: '/assets/icons/192x192.png',
          badge: '/assets/icons/48x48.png',
          vibrate: [100, 50, 100],
          data: {
            url: options?.data?.url || '/'
          },
          ...options
        };

        await registration.showNotification(title, notificationOptions);
      } catch (error) {
        console.error('Error showing notification:', error);
      }
    } else {
      console.log('Notification permission not granted');
    }
  }

  public async getSubscription(): Promise<PushSubscription | null> {
    try {
      const registration = await this.registerServiceWorker();

      if (!registration.pushManager) {
        return null;
      }

      return await registration.pushManager.getSubscription();
    } catch (error) {
      console.error('Error checking for active subscription:', error);
      return null;
    }
  }

  public async updateAuroraLevel(auroraLevel: string): Promise<void> {
    const subscription = await this.getSubscription();

    if (!subscription) {
      throw new Error('No subscription found');
    }

    // Update local state
    this.auroraLevel = auroraLevel.toUpperCase();
    this.saveSettings();

    // Always use subscribe endpoint with full payload
    const payload = this.getFullSubscriptionPayload(subscription);

    const response = await fetch(`${SubscriptionService.BASE_URL}/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Failed to update aurora level on server: ${response.status} ${response.statusText}`);
    }

    console.log('Successfully updated aurora level on server');
  }

  public async updateStationList(stationCodes: string[]): Promise<void> {
    const subscription = await this.getSubscription();

    if (!subscription) {
      throw new Error('No subscription found');
    }

    // Update local state
    this.stations = stationCodes;
    this.saveSettings();

    // Always use subscribe endpoint with full payload
    const payload = this.getFullSubscriptionPayload(subscription);

    const response = await fetch(`${SubscriptionService.BASE_URL}/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Failed to update station list on server: ${response.status} ${response.statusText}`);
    }

    console.log('Successfully updated station list on server');
  }

  public async unsubscribe(): Promise<void> {
    const subscription = await this.getSubscription();

    if (!subscription) {
      throw new Error('No subscription found');
    }

    const payload = {
      endpoint: subscription.endpoint,
      p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
      auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!))),
    };

    // Notify the server before unsubscribing locally. If this fetch throws
    // (offline, DNS failure, etc.), the local subscription stays intact so
    // the caller can retry instead of orphaning it on the server.
    const response = await fetch(`${SubscriptionService.BASE_URL}/unsubscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (response.status === 404) {
      console.log('Subscription not found on server, considered unsubscribed');
    } else if (!response.ok) {
      throw new Error(`Failed to unsubscribe on server: ${response.status} ${response.statusText}`);
    } else {
      console.log('Successfully unsubscribed on server');
    }

    // Only unsubscribe locally once the server acknowledged (or already
    // considers it gone).
    await subscription.unsubscribe();
  }

  // Deprecated
  public static async getLastLocationNotification(): Promise<any | null> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_CONFIG.NAME, DB_CONFIG.VERSION);
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(DB_CONFIG.STORES.LAST_LOCATION_NOTIFICATION)) {
          db.createObjectStore(DB_CONFIG.STORES.LAST_LOCATION_NOTIFICATION);
        }
        if (!db.objectStoreNames.contains(DB_CONFIG.STORES.USER_LOCATION)) {
          db.createObjectStore(DB_CONFIG.STORES.USER_LOCATION);
        }
      };
      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const tx = db.transaction(DB_CONFIG.STORES.LAST_LOCATION_NOTIFICATION, 'readonly');
        const store = tx.objectStore(DB_CONFIG.STORES.LAST_LOCATION_NOTIFICATION);
        const getReq = store.get('value');
        getReq.onsuccess = () => resolve(getReq.result || null);
        getReq.onerror = (e) => reject(e);
      };
      request.onerror = (e) => reject(e);
    });
  }

  // Deprecated
  public static async removeLastLocationNotification(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_CONFIG.NAME, DB_CONFIG.VERSION);
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(DB_CONFIG.STORES.LAST_LOCATION_NOTIFICATION)) {
          db.createObjectStore(DB_CONFIG.STORES.LAST_LOCATION_NOTIFICATION);
        }
        if (!db.objectStoreNames.contains(DB_CONFIG.STORES.USER_LOCATION)) {
          db.createObjectStore(DB_CONFIG.STORES.USER_LOCATION);
        }
      };
      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const tx = db.transaction(DB_CONFIG.STORES.LAST_LOCATION_NOTIFICATION, 'readwrite');
        const store = tx.objectStore(DB_CONFIG.STORES.LAST_LOCATION_NOTIFICATION);
        const delReq = store.delete('value');
        delReq.onsuccess = () => resolve();
        delReq.onerror = (e) => reject(e);
      };
      request.onerror = (e) => reject(e);
    });
  }
}